import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperIconComponent, PaperButtonComponent } from '@ricoh-paper/angular';
import { BmpCategoryPickerComponent } from './bmp-category-picker.component';
import { BmpTagInputComponent } from './bmp-tag-input.component';
import type { FlowProject } from '../../../../../core/services/flow-projects.service';

/**
 * BmpClassifyRowComponent — shared classify-row atom: category +
 * tags + Flow project pill. Used in both create-mode (just under
 * the title input, writes to BookMeetingService form state) AND
 * view-mode (under the hero meta strip, writes route via the
 * viewer-overlay save methods so the viewer can attach private
 * context to meetings they don't organise).
 *
 * The component owns Flow picker state internally; the parent owns
 * the actual write side-effects via the change outputs. This keeps
 * the component reusable across surfaces — create-mode wires
 * changes through the canonical service signals (which feed the
 * createBooking payload), view-mode wires changes through the
 * overlay mutations.
 */
@Component({
  selector: 'bmp-classify-row',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PaperIconComponent,
    PaperButtonComponent,
    BmpCategoryPickerComponent,
    BmpTagInputComponent,
    TranslocoPipe,
  ],
  styleUrl: './bmp-classify-row.component.scss',
  template: `
    <div class="bmp-classify" data-testid="bmp-classify-row">
      <bmp-category-picker
        [selectedId]="categoryId()"
        (categoryPicked)="categoryChanged.emit($event)" />
      <bmp-tag-input
        [tags]="tags()"
        (tagsChanged)="tagsChanged.emit($event)" />
      <!-- Flow project names are masked in session replay, and kept out of
           analytics by ph-no-capture: posthog-js folds a nested span's text
           into the button's own, where ph-mask cannot reach it (tv45.100). -->
      <div class="bmp-classify__flow-wrap ph-no-capture">
        @if (activeFlowProject(); as proj) {
          <button
            type="button"
            class="bmp__project-chip bmp__flow-active-chip"
            [style.--project-accent]="proj.colour || null"
            (click)="onOpenFlowPicker()">
            @if (proj.icon) {
              <span class="bmp__project-chip-icon">{{ proj.icon }}</span>
            } @else {
              <span class="bmp__project-chip-dot"></span>
            }
            <span class="bmp__project-chip-name ph-mask">{{ proj.name }}</span>
            <paper-icon name="chevron-down" [size]="11" aria-hidden="true" />
          </button>
        } @else {
          <button
            type="button"
            class="bmp__project-add bmp__flow-pill-trigger"
            data-testid="flow-pill-trigger"
            (click)="onOpenFlowPicker()">
            <paper-icon name="box" [size]="11" aria-hidden="true" />
            {{ 'viewer.book_meeting_panel.classify_row.add_to_flow' | transloco }}
            <paper-icon name="chevron-down" [size]="10" aria-hidden="true" />
          </button>
        }

        @if (flowPickerOpen()) {
          <div
            class="bmp__project-popover bmp__flow-pill-popover"
            data-testid="flow-project-picker"
            (keydown.escape)="closeFlowPicker()">
            <div class="bmp__project-popover-header">
              <span class="bmp__project-popover-title">
                {{ 'viewer.book_meeting_panel.classify_row.tag_into_project' | transloco }}
              </span>
              <paper-button
                class="bmp__project-popover-close"
                intent="ghost" shape="icon" size="xs"
                icon="x" [iconSize]="12"
                [ariaLabel]="'viewer.book_meeting_panel.classify_row.cancel' | transloco"
                (click)="closeFlowPicker()" />
            </div>

            <input
              type="text"
              class="bmp__project-popover-search"
              [placeholder]="'viewer.book_meeting_panel.classify_row.search_projects' | transloco"
              [value]="flowPickerQuery()"
              (input)="flowPickerQuery.set($any($event.target).value)" />

            @let suggestions = flowPickerSuggestions();
            @if (suggestions.length === 0 && !flowPickerCreating()) {
              <p class="bmp__project-popover-empty ph-mask">
                @if (flowPickerQuery()) {
                  {{ 'viewer.book_meeting_panel.classify_row.no_projects_match' | transloco: { query: flowPickerQuery() } }}
                } @else {
                  {{ 'viewer.book_meeting_panel.classify_row.no_projects_yet' | transloco }}
                }
              </p>
            } @else {
              <ul class="bmp__project-popover-list ph-mask" role="listbox">
                @for (project of suggestions; track project._id) {
                  @let isActive = flowProjectId() === project._id;
                  <li>
                    <button
                      type="button"
                      class="bmp__project-popover-option"
                      [class.bmp__project-popover-option--attached]="isActive"
                      [attr.aria-pressed]="isActive"
                      (click)="onPickProject(project._id)">
                      <paper-icon
                        [name]="isActive ? 'check-square' : 'square'"
                        [size]="14" />
                      <span class="bmp__project-popover-option-text">
                        <span class="bmp__project-popover-option-name">
                          @if (project.icon) {
                            <span>{{ project.icon }}</span>
                          }
                          {{ project.name }}
                        </span>
                        <span class="bmp__project-popover-option-meta">
                          {{ 'viewer.book_meeting_panel.classify_row.n_meetings' | transloco: { count: project.meetingCount } }}
                        </span>
                      </span>
                    </button>
                  </li>
                }
              </ul>
            }

            @if (flowPickerCreating()) {
              <div class="bmp__project-popover-create">
                <input
                  type="text"
                  class="bmp__project-popover-search"
                  [placeholder]="'viewer.book_meeting_panel.classify_row.project_name' | transloco"
                  [value]="flowPickerNewName()"
                  (input)="flowPickerNewName.set($any($event.target).value)"
                  (keydown.enter)="commitNewProject()" />
                <div class="bmp__project-popover-actions">
                  <button
                    type="button"
                    class="bmp__project-popover-cancel"
                    (click)="flowPickerCreating.set(false)">
                    {{ 'viewer.book_meeting_panel.classify_row.cancel' | transloco }}
                  </button>
                  <button
                    type="button"
                    class="bmp__project-popover-save"
                    [disabled]="!flowPickerNewName().trim()"
                    (click)="commitNewProject()">
                    {{ 'viewer.book_meeting_panel.classify_row.create_and_attach' | transloco }}
                  </button>
                </div>
              </div>
            } @else {
              <button
                type="button"
                class="bmp__project-popover-new"
                (click)="flowPickerCreating.set(true); flowPickerNewName.set(flowPickerQuery())">
                <paper-icon name="plus" [size]="11" />
                {{ 'viewer.book_meeting_panel.classify_row.create_new_project' | transloco }}
                @if (flowPickerQuery()) {
                  <span class="bmp__project-popover-new-hint ph-mask">
                    "{{ flowPickerQuery() }}"
                  </span>
                }
              </button>
            }
          </div>
        }
      </div>
    </div>
  `,
  // Reuses the existing .bmp__project-chip / .bmp__project-popover /
  // .bmp__flow-* classes already defined in book-meeting-panel.scss.
  // Only the wrapper rules need to live here so the row layout is
  // self-contained when this component is dropped onto a new
  // surface.
  styles: `
    :host { display: contents; }
    .bmp-classify {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
    }
    .bmp-classify__flow-wrap {
      position: relative;
      display: inline-flex;
      align-items: center;
    }
  `,
})
export class BmpClassifyRowComponent {
  /** Currently-selected UserCategory._id, or null when nothing chosen. */
  readonly categoryId = input<string | null>(null);

  /** Free-form viewer tags. */
  readonly tags = input<string[]>([]);

  /** Currently-attached Flow project _id (single-select for this row;
   *  the full meetingProjects[] cluster lives elsewhere on the panel). */
  readonly flowProjectId = input<string | null>(null);

  /** Active projects the picker shows. */
  readonly flowProjects = input<FlowProject[]>([]);

  /** Panel mode — kept for future variants (e.g. disable Flow pill
   *  in view-mode for read-only viewers). Today every viewer can
   *  tag privately so the cluster always renders. */
  readonly mode = input<'view' | 'create'>('create');

  // ── Outputs ───────────────────────────────────────────────
  readonly categoryChanged = output<string | null>();
  readonly tagsChanged = output<string[]>();
  /** Emitted when the viewer picks (or un-picks) a project. Same id
   *  twice toggles off — the parent decides whether to call attach
   *  or detach based on `flowProjectId` state at emit time. */
  readonly flowProjectChanged = output<string | null>();
  /** Emitted when the viewer types a new project name into the
   *  inline-create form. Parent creates the project + attaches. */
  readonly newProjectRequested = output<string>();
  /** Emitted the moment the Flow picker opens. Lets the parent
   *  trigger a lazy `flowProjects.load()` if it hasn't already, so
   *  the picker has data on first render instead of showing the
   *  empty-state copy. The parent's load() is idempotent. */
  readonly flowPickerOpened = output<void>();

  // ── Internal Flow picker state ────────────────────────────
  protected readonly flowPickerOpen = signal(false);
  protected readonly flowPickerQuery = signal('');
  protected readonly flowPickerCreating = signal(false);
  protected readonly flowPickerNewName = signal('');

  protected readonly activeFlowProject = computed(() => {
    const id = this.flowProjectId();
    if (!id) return null;
    return this.flowProjects().find(p => p._id === id) ?? null;
  });

  protected readonly flowPickerSuggestions = computed(() => {
    const q = this.flowPickerQuery().trim().toLowerCase();
    const all = this.flowProjects();
    if (!q) return all;
    return all.filter(
      p =>
        p.name.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q),
    );
  });

  protected onOpenFlowPicker(): void {
    this.flowPickerOpen.set(true);
    this.flowPickerQuery.set('');
    this.flowPickerCreating.set(false);
    // Tell the parent to lazy-load the viewer's FlowProjects list if
    // it hasn't already. Parent's load() is idempotent.
    this.flowPickerOpened.emit();
  }

  protected closeFlowPicker(): void {
    this.flowPickerOpen.set(false);
    this.flowPickerCreating.set(false);
    this.flowPickerNewName.set('');
  }

  protected onPickProject(projectId: string): void {
    const current = this.flowProjectId();
    // Toggle behaviour — same id twice clears the attachment.
    this.flowProjectChanged.emit(current === projectId ? null : projectId);
    this.closeFlowPicker();
  }

  protected commitNewProject(): void {
    const name = this.flowPickerNewName().trim();
    if (!name) return;
    this.newProjectRequested.emit(name);
    this.closeFlowPicker();
  }
}
